import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { ListChecks, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Margin tab: which quote-gate justifications are used most, on which deals and by whom. */
export function JustificationInsightsCard({ onOpenDeal }) {
  const [data, setData] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/justifications?days=365`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  const rows = data?.rows || [];
  const max = rows[0]?.count || 1;
  return (
    <Card className="border-slate-200" data-testid="justification-insights-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <ListChecks className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Quote gate justifications</h3>
          <span className="text-xs text-slate-400">· {data?.total_acks ?? '—'} acknowledgement(s) in the last 12 months</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">Reasons reps gave when confirming “Factors reviewed” on a flagged quote. Click a reason to see the deals and who signed off.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">Could not load justification insights.</p>}
        {data && !data.error && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="justification-insights-empty">No flagged quotes have been sent yet.</p>}
        {rows.length > 0 && (
          <ul className="space-y-1.5">
            {rows.map((r, i) => (
              <li key={r.justification} data-testid={`justification-insight-row-${i}`}>
                <button type="button" onClick={() => setOpenRow(openRow === i ? null : i)} className="w-full text-left" data-testid={`justification-insight-toggle-${i}`}>
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium text-[#0F2040] truncate">{r.justification}{!r.saved && <span className="ml-1.5 text-[10px] font-normal text-slate-400">· free text</span>}</span>
                    <span className="tabular-nums text-slate-600 shrink-0">{r.count}× · {r.deals.length} deal{r.deals.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="h-1.5 rounded bg-violet-100 mt-1"><div className="h-1.5 rounded bg-violet-500" style={{ width: `${Math.round((r.count / max) * 100)}%` }} /></div>
                </button>
                {openRow === i && (
                  <div className="mt-1.5 ml-2 pl-3 border-l-2 border-violet-200 text-[11px] text-slate-600 space-y-1" data-testid={`justification-insight-detail-${i}`}>
                    <div className="flex flex-wrap gap-1">
                      {r.deals.map((d) => (
                        <button key={d.id} type="button" onClick={() => onOpenDeal?.(d.id)} className="px-1.5 py-0 rounded border border-slate-200 bg-white hover:border-violet-300 text-[#0F2040]" data-testid={`justification-insight-deal-${d.id}`}>
                          {d.name} <span className="text-slate-400">×{d.count}</span>
                        </button>
                      ))}
                    </div>
                    <p><span className="text-slate-400">Factors:</span> {r.factors.map((f) => `${f.label} (${f.count})`).join(', ') || '—'} · <span className="text-slate-400">Signed off by:</span> {r.by.map((b) => `${b.name} (${b.count})`).join(', ')}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {data?.unused_saved?.length > 0 && <p className="text-[10px] text-slate-400 mt-3" data-testid="justification-insights-unused">Saved but never used: {data.unused_saved.join(' · ')}</p>}
      </CardContent>
    </Card>
  );
}
